import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../bizsuite-app';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const COMP_TYPES = ['earning', 'deduction'];
const CALC_TYPES = ['flat', 'percentage'];

export default function Salary() {
  const [tab, setTab] = useState('batches');

  return (
    <div>
      <div className="tabs" style={{ marginBottom: 16 }}>
        {[
          { key: 'batches', label: 'Salary Batches' },
          { key: 'components', label: 'Components Setup' },
          { key: 'slips', label: 'Salary Slips' },
        ].map(t => (
          <button key={t.key} className={`tab-btn ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </div>
      {tab === 'batches' && <SalaryBatches />}
      {tab === 'components' && <SalaryComponents />}
      {tab === 'slips' && <SalarySlips />}
    </div>
  );
}

/* =================================================================
   Salary Batches Tab
   ================================================================= */
function SalaryBatches() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processModal, setProcessModal] = useState(false);
  const [processForm, setProcessForm] = useState({ month: new Date().getMonth() + 1, year: new Date().getFullYear() });
  const [processing, setProcessing] = useState(false);
  const [detailBatch, setDetailBatch] = useState(null);
  const [detailSlips, setDetailSlips] = useState([]);

  const fetchBatches = useCallback(async () => {
    try {
      const res = await apiFetch('/api/salary/salary/batches');
      if (res.ok) {
        const data = await res.json();
        setBatches(data.batches || []);
      }
    } catch (e) { /* handled */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchBatches(); }, [fetchBatches]);

  async function handleProcess() {
    setProcessing(true);
    try {
      const res = await apiFetch('/api/salary/salary/process', {
        method: 'POST',
        body: JSON.stringify(processForm)
      });
      if (res.ok) {
        setProcessModal(false);
        fetchBatches();
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to process');
      }
    } catch (e) { /* handled */ }
    setProcessing(false);
  }

  async function fetchDetail(batch) {
    try {
      const res = await apiFetch(`/api/salary/salary/batches/${batch.id}`);
      if (res.ok) {
        const data = await res.json();
        setDetailBatch(data.batch);
        setDetailSlips(data.slips || []);
      }
    } catch (e) { /* handled */ }
  }

  async function handleApprove(id) {
    await apiFetch(`/api/salary/salary/batches/${id}/approve`, { method: 'POST' });
    fetchBatches();
    if (detailBatch?.id === id) fetchDetail(detailBatch);
  }

  async function handlePay(id) {
    if (!confirm('Mark this batch as paid?')) return;
    await apiFetch(`/api/salary/salary/batches/${id}/pay`, { method: 'POST', body: JSON.stringify({}) });
    fetchBatches();
    if (detailBatch?.id === id) fetchDetail(detailBatch);
  }

  // Detail view
  if (detailBatch) {
    return (
      <div>
        <button className="btn btn-secondary" onClick={() => setDetailBatch(null)} style={{ marginBottom: 16 }}>Back to Batches</button>
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <h4>{MONTHS[(detailBatch.month || 1) - 1]} {detailBatch.year}</h4>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span className={`badge ${detailBatch.status === 'paid' ? 'badge-green' : detailBatch.status === 'approved' ? 'badge-blue' : 'badge-gray'}`}>{detailBatch.status}</span>
              {detailBatch.status === 'draft' && <button className="btn btn-sm btn-primary" onClick={() => handleApprove(detailBatch.id)}>Approve</button>}
              {detailBatch.status === 'approved' && <button className="btn btn-sm btn-primary" onClick={() => handlePay(detailBatch.id)}>Mark Paid</button>}
            </div>
          </div>
          <div className="card-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div><strong>Employees:</strong> {detailBatch.employee_count}</div>
            <div><strong>Total Amount:</strong> {parseFloat(detailBatch.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
            <div><strong>Paid Date:</strong> {detailBatch.paid_date || '-'}</div>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h4>Salary Slips</h4></div>
          <table className="data-table">
            <thead><tr><th>Employee</th><th>Code</th><th style={{ textAlign: 'right' }}>Gross</th><th style={{ textAlign: 'right' }}>Deductions</th><th style={{ textAlign: 'right' }}>Net Pay</th><th>Status</th></tr></thead>
            <tbody>
              {detailSlips.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: '#94a3b8' }}>No slips</td></tr>
              ) : detailSlips.map(s => (
                <tr key={s.id}>
                  <td>{s.first_name} {s.last_name}</td>
                  <td>{s.emp_code || '-'}</td>
                  <td style={{ textAlign: 'right' }}>{parseFloat(s.gross || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td style={{ textAlign: 'right' }}>{parseFloat(s.total_deductions || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{parseFloat(s.net || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td><span className={`badge ${s.status === 'paid' ? 'badge-green' : 'badge-gray'}`}>{s.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button className="btn btn-primary" onClick={() => setProcessModal(true)}>Process Salary</button>
      </div>

      <div className="card">
        <table className="data-table">
          <thead><tr><th>Period</th><th style={{ textAlign: 'right' }}>Employees</th><th style={{ textAlign: 'right' }}>Total Amount</th><th>Status</th><th>Paid Date</th><th>Actions</th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ textAlign: 'center' }}>Loading...</td></tr>
            ) : batches.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: '#94a3b8' }}>No salary batches</td></tr>
            ) : batches.map(b => (
              <tr key={b.id}>
                <td><a href="#" onClick={e => { e.preventDefault(); fetchDetail(b); }} style={{ color: '#6366f1', fontWeight: 500 }}>{MONTHS[(b.month || 1) - 1]} {b.year}</a></td>
                <td style={{ textAlign: 'right' }}>{b.employee_count}</td>
                <td style={{ textAlign: 'right' }}>{parseFloat(b.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td><span className={`badge ${b.status === 'paid' ? 'badge-green' : b.status === 'approved' ? 'badge-blue' : 'badge-gray'}`}>{b.status}</span></td>
                <td>{b.paid_date || '-'}</td>
                <td>
                  <button className="btn btn-sm btn-secondary" onClick={() => fetchDetail(b)} style={{ marginRight: 4 }}>View</button>
                  {b.status === 'draft' && <button className="btn btn-sm btn-primary" onClick={() => handleApprove(b.id)} style={{ marginRight: 4 }}>Approve</button>}
                  {b.status === 'approved' && <button className="btn btn-sm btn-primary" onClick={() => handlePay(b.id)}>Pay</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {processModal && (
        <div className="modal-overlay" onClick={() => setProcessModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div className="modal-header"><h3>Process Salary</h3><button className="modal-close" onClick={() => setProcessModal(false)}>X</button></div>
            <div className="modal-body">
              <div className="form-group">
                <label>Month *</label>
                <select className="form-control" value={processForm.month} onChange={e => setProcessForm({ ...processForm, month: parseInt(e.target.value) })}>
                  {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Year *</label>
                <input type="number" className="form-control" value={processForm.year} onChange={e => setProcessForm({ ...processForm, year: parseInt(e.target.value) })} />
              </div>
              <p style={{ color: '#64748b', fontSize: 13 }}>This will generate salary slips for all active employees based on their compensation data.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setProcessModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleProcess} disabled={processing}>{processing ? 'Processing...' : 'Process'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* =================================================================
   Salary Components Tab
   ================================================================= */
function SalaryComponents() {
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ name: '', type: 'earning', calculation_type: 'flat', percentage_of: '', statutory: false });
  const [editing, setEditing] = useState(null);

  const fetchComponents = useCallback(async () => {
    try {
      const res = await apiFetch('/api/salary/salary-components');
      if (res.ok) {
        const data = await res.json();
        setComponents(data.components || []);
      }
    } catch (e) { /* handled */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchComponents(); }, [fetchComponents]);

  async function handleSave() {
    try {
      const method = editing ? 'PUT' : 'POST';
      const url = editing ? `/api/salary/salary-components/${editing.id}` : '/api/salary/salary-components';
      const res = await apiFetch(url, { method, body: JSON.stringify(form) });
      if (res.ok) {
        setModalOpen(false);
        setEditing(null);
        fetchComponents();
      }
    } catch (e) { /* handled */ }
  }

  async function handleDelete(id) {
    if (!confirm('Delete this component?')) return;
    await apiFetch(`/api/salary/salary-components/${id}`, { method: 'DELETE' });
    fetchComponents();
  }

  function openEdit(comp) {
    setEditing(comp);
    setForm({ name: comp.name, type: comp.type, calculation_type: comp.calculation_type || 'flat', percentage_of: comp.percentage_of || '', statutory: comp.statutory || false });
    setModalOpen(true);
  }

  const earnings = components.filter(c => c.type === 'earning');
  const deductions = components.filter(c => c.type === 'deduction');

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button className="btn btn-primary" onClick={() => { setEditing(null); setForm({ name: '', type: 'earning', calculation_type: 'flat', percentage_of: '', statutory: false }); setModalOpen(true); }}>+ New Component</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="card">
          <div className="card-header"><h4>Earnings</h4></div>
          <table className="data-table">
            <thead><tr><th>Name</th><th>Calc</th><th>Statutory</th><th>Actions</th></tr></thead>
            <tbody>
              {earnings.length === 0 ? (
                <tr><td colSpan={4} style={{ textAlign: 'center', color: '#94a3b8' }}>No earnings</td></tr>
              ) : earnings.map(c => (
                <tr key={c.id}>
                  <td>{c.name}</td>
                  <td>{c.calculation_type}{c.percentage_of ? ` of ${c.percentage_of}` : ''}</td>
                  <td>{c.statutory ? 'Yes' : 'No'}</td>
                  <td>
                    <button className="btn btn-sm btn-secondary" onClick={() => openEdit(c)} style={{ marginRight: 4 }}>Edit</button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(c.id)}>Del</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card">
          <div className="card-header"><h4>Deductions</h4></div>
          <table className="data-table">
            <thead><tr><th>Name</th><th>Calc</th><th>Statutory</th><th>Actions</th></tr></thead>
            <tbody>
              {deductions.length === 0 ? (
                <tr><td colSpan={4} style={{ textAlign: 'center', color: '#94a3b8' }}>No deductions</td></tr>
              ) : deductions.map(c => (
                <tr key={c.id}>
                  <td>{c.name}</td>
                  <td>{c.calculation_type}{c.percentage_of ? ` of ${c.percentage_of}` : ''}</td>
                  <td>{c.statutory ? 'Yes' : 'No'}</td>
                  <td>
                    <button className="btn btn-sm btn-secondary" onClick={() => openEdit(c)} style={{ marginRight: 4 }}>Edit</button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(c.id)}>Del</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 450 }}>
            <div className="modal-header"><h3>{editing ? 'Edit Component' : 'New Component'}</h3><button className="modal-close" onClick={() => setModalOpen(false)}>X</button></div>
            <div className="modal-body">
              <div className="form-group"><label>Name *</label><input className="form-control" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label>Type</label>
                  <select className="form-control" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                    {COMP_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Calculation</label>
                  <select className="form-control" value={form.calculation_type} onChange={e => setForm({ ...form, calculation_type: e.target.value })}>
                    {CALC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              {form.calculation_type === 'percentage' && (
                <div className="form-group"><label>Percentage Of</label><input className="form-control" value={form.percentage_of} onChange={e => setForm({ ...form, percentage_of: e.target.value })} placeholder="e.g. basic" /></div>
              )}
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={form.statutory} onChange={e => setForm({ ...form, statutory: e.target.checked })} />
                  Statutory Component
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={!form.name}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* =================================================================
   Salary Slips Tab
   ================================================================= */
function SalarySlips() {
  const [slips, setSlips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [detailSlip, setDetailSlip] = useState(null);

  const fetchSlips = useCallback(async () => {
    setLoading(true);
    try {
      let url = '/api/salary/salary/slips';
      const params = [];
      if (filterMonth) params.push(`month=${filterMonth}`);
      if (filterYear) params.push(`year=${filterYear}`);
      if (params.length) url += '?' + params.join('&');
      const res = await apiFetch(url);
      if (res.ok) {
        const data = await res.json();
        setSlips(data.slips || []);
      }
    } catch (e) { /* handled */ }
    setLoading(false);
  }, [filterMonth, filterYear]);

  useEffect(() => { fetchSlips(); }, [fetchSlips]);

  async function fetchSlipDetail(slip) {
    try {
      const res = await apiFetch(`/api/salary/salary/slips/${slip.id}`);
      if (res.ok) {
        const data = await res.json();
        setDetailSlip(data.slip);
      }
    } catch (e) { /* handled */ }
  }

  if (detailSlip) {
    let components = {};
    try { components = typeof detailSlip.components === 'string' ? JSON.parse(detailSlip.components) : (detailSlip.components || {}); } catch (e) { /* handled */ }

    return (
      <div>
        <button className="btn btn-secondary" onClick={() => setDetailSlip(null)} style={{ marginBottom: 16 }}>Back to Slips</button>
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <h4>Salary Slip - {detailSlip.first_name} {detailSlip.last_name}</h4>
            <span className={`badge ${detailSlip.status === 'paid' ? 'badge-green' : 'badge-gray'}`}>{detailSlip.status}</span>
          </div>
          <div className="card-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div><strong>Employee Code:</strong> {detailSlip.emp_code || '-'}</div>
            <div><strong>Department:</strong> {detailSlip.department_name || '-'}</div>
            <div><strong>Period:</strong> {MONTHS[(detailSlip.month || 1) - 1]} {detailSlip.year}</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div className="card">
            <div className="card-header"><h4>Earnings</h4></div>
            <table className="data-table">
              <thead><tr><th>Component</th><th style={{ textAlign: 'right' }}>Amount</th></tr></thead>
              <tbody>
                {components.earnings ? Object.entries(components.earnings).map(([key, val]) => (
                  <tr key={key}><td>{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</td><td style={{ textAlign: 'right' }}>{parseFloat(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td></tr>
                )) : <tr><td colSpan={2} style={{ textAlign: 'center', color: '#94a3b8' }}>-</td></tr>}
                <tr style={{ fontWeight: 700, borderTop: '2px solid #e2e8f0' }}>
                  <td>Gross</td>
                  <td style={{ textAlign: 'right' }}>{parseFloat(detailSlip.gross || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="card">
            <div className="card-header"><h4>Deductions</h4></div>
            <table className="data-table">
              <thead><tr><th>Component</th><th style={{ textAlign: 'right' }}>Amount</th></tr></thead>
              <tbody>
                {components.deductions ? Object.entries(components.deductions).map(([key, val]) => (
                  <tr key={key}><td>{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</td><td style={{ textAlign: 'right' }}>{parseFloat(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td></tr>
                )) : <tr><td colSpan={2} style={{ textAlign: 'center', color: '#94a3b8' }}>-</td></tr>}
                <tr style={{ fontWeight: 700, borderTop: '2px solid #e2e8f0' }}>
                  <td>Total Deductions</td>
                  <td style={{ textAlign: 'right' }}>{parseFloat(detailSlip.total_deductions || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="card" style={{ marginTop: 16, background: '#f0fdf4' }}>
          <div className="card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 20 }}>
            <h3 style={{ margin: 0 }}>Net Pay</h3>
            <span style={{ fontSize: 24, fontWeight: 700, color: '#10b981' }}>
              {parseFloat(detailSlip.net || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <select className="form-control" value={filterMonth} onChange={e => setFilterMonth(e.target.value)} style={{ width: 160 }}>
          <option value="">All Months</option>
          {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>
        <input type="number" className="form-control" value={filterYear} onChange={e => setFilterYear(e.target.value)} placeholder="Year" style={{ width: 100 }} />
      </div>

      <div className="card">
        <table className="data-table">
          <thead><tr><th>Employee</th><th>Code</th><th>Period</th><th style={{ textAlign: 'right' }}>Gross</th><th style={{ textAlign: 'right' }}>Net</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ textAlign: 'center' }}>Loading...</td></tr>
            ) : slips.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', color: '#94a3b8' }}>No salary slips</td></tr>
            ) : slips.map(s => (
              <tr key={s.id}>
                <td>{s.first_name} {s.last_name}</td>
                <td>{s.emp_code || '-'}</td>
                <td>{MONTHS[(s.month || 1) - 1]} {s.year}</td>
                <td style={{ textAlign: 'right' }}>{parseFloat(s.gross || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>{parseFloat(s.net || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td><span className={`badge ${s.status === 'paid' ? 'badge-green' : 'badge-gray'}`}>{s.status}</span></td>
                <td><button className="btn btn-sm btn-secondary" onClick={() => fetchSlipDetail(s)}>View</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
